import { API_BASE_URL } from '../../../config/env'
import { fetchWithAuth } from '../../fetchWithAuth'

// Updated to include fallback to sessionStorage if localStorage is blocked
const getAuthToken = () => {
  try {
    const token = localStorage.getItem('authToken')
    if (token) return token

    // Try session storage as fallback
    return sessionStorage.getItem('authToken') || ''
  } catch (error) {
    console.warn('Error accessing storage:', error)
    try {
      // If localStorage fails, try sessionStorage
      return sessionStorage.getItem('authToken') || ''
    } catch (e) {
      console.error('All storage methods failed:', e)
      return ''
    }
  }
}

/**
 * Fetch the authenticated user's profile information
 * @returns {Promise<Object>} User profile data
 */
export const getProfile = async () => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/me`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      // Add credentials mode to ensure cookies are sent
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)

      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }

      throw new Error(errorData.message || `Failed to fetch profile. Status: ${response.status}`)
    }

    const data = await response.json()
    return data.user
  } catch (error) {
    console.error('Error fetching profile:', error)
    throw error
  }
}

export const getAllUsers = async () => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Add credentials mode to ensure cookies are sent
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)

      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }

      throw new Error(errorData.message || `Failed to fetch profile. Status: ${response.status}`)
    }

    const data = await response.json()
    return data.users
  } catch (error) {
    console.error('Error fetching profile:', error)
    throw error
  }
}

export const getUsers = async () => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/dropdown`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Add credentials mode to ensure cookies are sent
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)

      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }

      throw new Error(errorData.message || `Failed to fetch profile. Status: ${response.status}`)
    }

    const data = await response.json()
    return data.users
  } catch (error) {
    console.error('Error fetching profile:', error)
    throw error
  }
}

export const getUserById = async userId => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/${userId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Add credentials mode to ensure cookies are sent
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)

      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }

      throw new Error(errorData.message || `Failed to fetch user. Status: ${response.status}`)
    }
    const data = await response.json()
    return data.user
  } catch (error) {
    console.error('Error parsing user data:', error)
    throw new Error('Failed to parse user data')
  }
}

export const updateUserById = async (userId, updateData) => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/${userId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'same-origin',
      body: JSON.stringify(updateData),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)

      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }

      throw new Error(errorData.message || `Failed to update user. Status: ${response.status}`)
    }

    const data = await response.json()
    return data.user || data // Adjust based on your API response structure
  } catch (error) {
    console.error('Error updating user:', error)
    throw error
  }
}

export const getAllRoleNameUser = async () => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/role/user`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      // Add credentials mode to ensure cookies are sent
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)

      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }

      throw new Error(errorData.message || `Failed to fetch profile. Status: ${response.status}`)
    }

    const data = await response.json()
    return data.users
  } catch (error) {
    console.error('Error fetching profile:', error)
    throw error
  }
}

/**
 * Update the authenticated user's profile
 * @param {FormData|Object} formDataOrProfileData - Either FormData object or profile data object
 * @param {File} [profilePicture] - Optional new profile picture file (only used if first param is Object)
 * @returns {Promise<Object>} Updated profile data
 */
export const updateProfile = async (formDataOrProfileData, profilePicture = null) => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    let formData

    // Check if the first parameter is already FormData
    if (formDataOrProfileData instanceof FormData) {
      formData = formDataOrProfileData
    } else {
      // Otherwise create FormData from the object
      formData = new FormData()
      Object.keys(formDataOrProfileData).forEach(key => {
        if (formDataOrProfileData[key] !== undefined && formDataOrProfileData[key] !== null) {
          formData.append(key, formDataOrProfileData[key])
        }
      })

      if (profilePicture) {
        formData.append('profile_picture', profilePicture)
      }
    }

    // Debug FormData contents (uncomment if needed)
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}: ${pair[1] instanceof File ? `File: ${pair[1].name}` : pair[1]}`)
    }

    const response = await fetchWithAuth(`${API_BASE_URL}/users/me`, {
      method: 'PATCH',
      body: formData,
      // Add credentials mode to ensure cookies are sent
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)
      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }
      throw new Error(errorData.message || `Failed to update profile. Status: ${response.status}`)
    }

    const data = await response.json()
    return data.user || data
  } catch (error) {
    console.error('Error updating profile:', error)
    throw error
  }
}

/**
 * Delete the authenticated user's profile picture
 * @returns {Promise<Object>} Success message
 */
export const deleteProfilePicture = async () => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/me/profile-picture`, {
      method: 'DELETE',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)
      if (response.status === 401) {
        throw new Error('Session expired. Please log in again.')
      }
      throw new Error(
        errorData.message || `Failed to delete profile picture. Status: ${response.status}`
      )
    }

    return await response.json()
  } catch (error) {
    console.error('Error deleting profile picture:', error)
    throw error
  }
}

export const getAllCenters = async () => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/centers`)
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    return await response.json()
  } catch (error) {
    console.error('Error fetching centers:', error)
    throw error
  }
}

export const getCenterById = async centerId => {
  const token = getAuthToken()
  if (!token) {
    throw new Error('No authentication token found. Please log in.')
  }

  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/centers/${centerId}`, {
      method: 'GET',
      credentials: 'same-origin',
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('API Error:', errorData)
      return null
    }

    const data = await response.json()
    return data.data || data
  } catch (error) {
    console.error('Error fetching center details:', error)
    return null
  }
}

export const getAllScreens = async () => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/screens`)
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    return await response.json()
  } catch (error) {
    console.error('Error fetching Screens:', error)
    throw error
  }
}

export const assignScreens = async (userId, screens) => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/${userId}/screens/bulk-update`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(screens),
    })

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }

    return await response.json()
  } catch (error) {
    console.error('Error assigning screens:', error)
    throw error
  }
}
export const getAssignedScreens = async userId => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/users/${userId}/screens`)
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    return await response.json()
  } catch (error) {
    console.error('Error fetching Screens:', error)
    throw error
  }
}

export const getWatchmanScreens = async userId => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/screens/operations`)
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    return await response.json()
  } catch (error) {
    console.error('Error fetching Screens:', error)
    throw error
  }
}

export const fetchUserScreens = async userId => {
  const token = getAuthToken()

  try {
    const res = await fetchWithAuth(`${API_BASE_URL}/users/${userId}/screens`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.message || 'Failed to fetch user screens')
    }
    const data = await res.json()
    return data.data || []
  } catch (err) {
    console.error('Error fetching user screens:', err)
    return []
  }
}

// Dropdown: employees without a linked user account
export const getEmployeesWithoutUser = async () => {
  try {
    const response = await fetchWithAuth(`${API_BASE_URL}/user-registration/employees`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching employees without user:', error)
    throw error
  }
}

// On selection: prefill form fields for a given employee
export const getEmployeeForRegistration = async employeeId => {
  try {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/user-registration/employees/${employeeId}`,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    return data
  } catch (error) {
    console.error('Error fetching employee for registration:', error)
    throw error
  }
}
